import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import Eshops from '@/sections/Eshops';
import Hero from '@/sections/Hero';
import { Testimonials as TestimonialsSection } from '@/sections/Testimonials';
import Benefits from '@/sections/revolvingLoan/Benefits';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import React, { useMemo } from 'react';
import { SeoHeader } from '@/components/SeoHeader';
import IconCheck from '@/assets/icons/heroIcons/Circle-check.svg';
import { type EshopsList, getAffiliateEshopsList } from '@/api/sanity/affiliateEshopsList';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import MaxiComparisonTable from '@/sections/revolvingLoan/MaxiComparisonTable';
import { BankSection } from '@/sections/revolvingLoan/BankSection';
import { FAQsSection } from '@/sections/FAQsSection';
import { getSectionAnchorId } from '@/utils/anchors';
import { useProductRegistrationButtons, useProductRegistrationLink } from '@/hooks/useProductRegistrationLink';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  eshops: EshopsList;
  testimonials: Testimonial[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(67);
  usePageView("revolving-loan");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "revolvingLoan", "eshops", "testimonials"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    eshops,
    pageBanner,
    menuPromoBanners,
    testimonials
  } = props;
  const registrationLink = useProductRegistrationLink("balicek-maxi");
  const heroButtons = useProductRegistrationButtons(pageBanner?.buttons, "balicek-maxi");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconCheck />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("revolvingLoan:heroSection.benefitsList.1");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="flex flex-row items-center gap-3">{t1}<p>{t2}</p></div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconCheck />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("revolvingLoan:heroSection.benefitsList.2");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <div className="flex flex-row items-center gap-3">{t4}<p>{t5}</p></div>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconCheck />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("revolvingLoan:heroSection.benefitsList.3");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="flex flex-row items-center gap-3">{t7}<p>{t8}</p></div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t6 || $[18] !== t9) {
    t10 = <>{t3}{t6}{t9}</>;
    $[16] = t3;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const heroText = t10;
  let t11;
  if ($[20] !== t) {
    t11 = t("revolvingLoan:seo.title");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("revolvingLoan:seo.description");
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <SeoHeader title={t11} description={t12} />;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== pageBanner?.title || $[28] !== t) {
    t14 = pageBanner?.title ?? t("revolvingLoan:heroSection.title");
    $[27] = pageBanner?.title;
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = pageBanner?.text ?? heroText;
  let t16;
  if ($[30] !== heroButtons || $[31] !== registrationLink || $[32] !== t) {
    t16 = heroButtons ?? [{
      ariaLabel: t("revolvingLoan:heroSection.buttonPrimary"),
      label: t("revolvingLoan:heroSection.buttonPrimary"),
      to: registrationLink,
      testId: "revolvingLoanHeroPrimaryCTA",
      analyticsParam: "revolvingLoanHeroPrimaryCTA",
      variant: "primary"
    }];
    $[30] = heroButtons;
    $[31] = registrationLink;
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = pageBanner?.image?.url ?? "/images/sections/revolvingLoan/hero/cs/hero.png";
  let t18;
  if ($[34] !== pageBanner?.image?.alt || $[35] !== t) {
    t18 = pageBanner?.image?.alt ?? t("revolvingLoan:heroSection.imageAlt");
    $[34] = pageBanner?.image?.alt;
    $[35] = t;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t17 || $[38] !== t18) {
    t19 = {
      url: t17,
      alt: t18
    };
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = pageBanner?.variant || "products";
  let t21;
  if ($[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t19 || $[44] !== t20) {
    t21 = <Hero title={t14} text={t15} buttons={t16} image={t19} variant={t20} />;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Benefits />;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = getSectionAnchorId("flexibleTable");
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <AnimatedSection bgColor="bg-blue-100" id={t23}><AnimatedContent className="pb-10 lg:pb-20"><MaxiComparisonTable /></AnimatedContent></AnimatedSection>;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = getSectionAnchorId("testimonials");
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== testimonials) {
    t26 = <TestimonialsSection testimonials={testimonials} />;
    $[50] = testimonials;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <BankSection />;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t26) {
    t28 = <AnimatedContent>{t26}{t27}</AnimatedContent>;
    $[53] = t26;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== eshops) {
    t29 = eshops.length > 0 && <AnimatedContent className="pb-10 lg:pb-20"><Eshops eshops={eshops} /></AnimatedContent>;
    $[55] = eshops;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28 || $[58] !== t29) {
    t30 = <AnimatedSection bgColor="bg-blue-50" className="overflow-hidden pb-0!" id={t25}><div className="relative z-10"><div className="flex flex-col gap-20 pb-10 lg:gap-40 lg:pb-20">{t28}{t29}</div></div></AnimatedSection>;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = getSectionAnchorId("faqSection");
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <AnimatedSection bgColor="bg-blue-100" id={t31}><AnimatedContent className="pt-10 lg:pt-20"><FAQsSection /></AnimatedContent></AnimatedSection>;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== menuPromoBanners || $[63] !== t13 || $[64] !== t21 || $[65] !== t30) {
    t33 = <Layout menuPromoBanners={menuPromoBanners}>{t13}{t21}{t22}{t24}{t30}{t32}</Layout>;
    $[62] = menuPromoBanners;
    $[63] = t13;
    $[64] = t21;
    $[65] = t30;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  return t33;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('balicek-maxi');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('revolving-loan'),
      eshops: await getAffiliateEshopsList('revolving-loan'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      testimonials: await getTestimonialsBySlug('revolving-loan'),
      ...(await getIntlProps(ctx, ['revolvingLoan', 'eshops', 'testimonials']))
    }
  };
}) satisfies GetStaticProps<Props>;